/**
 * Noise modifier — pure typed-array core (Three.js-free).
 *
 * Applies one or more layers of procedural 3D noise as per-axis vertex
 * displacement. Layers stack additively; each layer samples its own noise
 * field (Perlin, Ridged, or Voronoi F1) in **object-normalised space**
 * (positions divided by the mesh's characteristic size), writes the result
 * scaled by `layer.strength * charSize` into the axes gated by `axisX/Y/Z`,
 * and respects an optional per-layer vertex group mask.
 *
 * Both parameters are therefore dimensionless and object-relative:
 *   • `scale`    = approximate number of noise features spanning the object.
 *                  scale=1 → one large feature, scale=5 → five bumps.
 *   • `strength` = peak displacement as a fraction of the object's largest
 *                  dimension. strength=0.02 → 2% of object size.
 *
 * Topology is untouched — positions shift in place, indices, edges, UVs,
 * groups, topology, and per-vertex attributes pass through unchanged, and
 * the returned provenance is identity. This is the simplest modifier core
 * in the stack: pure vertex mutation.
 *
 * ## Determinism contract
 *
 * The noise field is sampled in **object-local space** — the input
 * `geo.positions` comes directly from `SceneObject.vertices`, which is
 * the untransformed mesh. Moving, rotating, or scaling the whole object
 * in the viewport does NOT change the local vertex positions, so the
 * noise field moves rigidly with the object as expected. Reloading a
 * scene produces identical output because:
 *   1. The same mesh vertices produce the same sample coordinates.
 *   2. All randomness flows through a 32-bit integer hash of
 *      `(seed, ix, iy, iz)` — no `Math.random()`, no stateful RNG, no
 *      time-dependence anywhere in the core.
 *
 * Editing vertices in edit mode, sculpt, or via a preceding modifier
 * WILL change the noise output for the affected vertices — this matches
 * the user's expectation that the field is "painted onto" the mesh.
 *
 * The step cache in `modifierApply.ts` keys on the input `positions`
 * Float32Array identity plus a quantized serialization of the modifier,
 * so this function must remain a pure function of its inputs.
 */

import type {
  BlendModifier,
  BlendNoiseModifier,
  NoiseLayer,
} from "../../blend-reader/types/BlendModifier.ts";
import { identityProvenance } from "../../modeling/provenance.ts";
import type { ModifierGeometry } from "./modifierTypes.ts";

// ---------------------------------------------------------------------------
// Integer hash & gradient tables
// ---------------------------------------------------------------------------

/**
 * 32-bit integer hash of a 3D lattice coordinate + seed.
 * Uses `Math.imul` so the multiplies stay in 32-bit lane (avoids JS
 * double-precision drift that would break reproducibility). Finalizer is
 * the well-known `xmxmx` mix (Murmur3 variant).
 */
function hash3(ix: number, iy: number, iz: number, seed: number): number {
  let h = (seed | 0) ^ 0x9e3779b1;
  h = Math.imul(h ^ (ix | 0), 0x85ebca6b);
  h = Math.imul(h ^ (iy | 0), 0xc2b2ae35);
  h = Math.imul(h ^ (iz | 0), 0x27d4eb2f);
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/** Hash → float in [0, 1). */
function hashToUnit(h: number): number {
  return (h >>> 0) / 4294967296;
}

/**
 * 12-direction gradient set (edge midpoints of a cube). Shared by both
 * Simplex and the cellular noise types (Voronoi, Worley).
 */
const GRAD3: readonly (readonly [number, number, number])[] = [
  [1, 1, 0],
  [-1, 1, 0],
  [1, -1, 0],
  [-1, -1, 0],
  [1, 0, 1],
  [-1, 0, 1],
  [1, 0, -1],
  [-1, 0, -1],
  [0, 1, 1],
  [0, -1, 1],
  [0, 1, -1],
  [0, -1, -1],
];

function gradDot(h: number, x: number, y: number, z: number): number {
  const g = GRAD3[h % 12];
  return g[0] * x + g[1] * y + g[2] * z;
}

// ---------------------------------------------------------------------------
// Simplex / Ridged noise
// ---------------------------------------------------------------------------

// Skew/unskew constants for the 3D simplex lattice.
const F3 = 1 / 3;
const G3 = 1 / 6;

/**
 * 3D Simplex gradient noise.
 *
 * Evaluates four simplex-cell corners rather than the eight cube-corners of
 * classic Perlin, using a radial `(0.6 − r²)⁴` falloff kernel.  The result
 * is more isotropic — no grid-aligned directional bias — and costs roughly
 * half the gradient lookups.  Output is in approximately [−1, 1]; scale
 * factor 32 is empirical for the GRAD3 gradient set with the 0.6 kernel.
 *
 * The original simplex patent (US6867776, filed 2001) expired in 2022.
 */
function simplex3(x: number, y: number, z: number, seed: number): number {
  // Skew the input into simplex lattice space.
  const s = (x + y + z) * F3;
  const i = Math.floor(x + s);
  const j = Math.floor(y + s);
  const k = Math.floor(z + s);

  // Unskew the lattice cell origin back to input space.
  const t = (i + j + k) * G3;
  const x0 = x - (i - t);
  const y0 = y - (j - t);
  const z0 = z - (k - t);

  // Determine which simplex tetrahedron we're in by ranking x0, y0, z0.
  let i1: number, j1: number, k1: number; // second simplex corner offset
  let i2: number, j2: number, k2: number; // third simplex corner offset
  if (x0 >= y0) {
    if (y0 >= z0) {
      i1 = 1;
      j1 = 0;
      k1 = 0;
      i2 = 1;
      j2 = 1;
      k2 = 0;
    } else if (x0 >= z0) {
      i1 = 1;
      j1 = 0;
      k1 = 0;
      i2 = 1;
      j2 = 0;
      k2 = 1;
    } else {
      i1 = 0;
      j1 = 0;
      k1 = 1;
      i2 = 1;
      j2 = 0;
      k2 = 1;
    }
  } else {
    if (y0 < z0) {
      i1 = 0;
      j1 = 0;
      k1 = 1;
      i2 = 0;
      j2 = 1;
      k2 = 1;
    } else if (x0 < z0) {
      i1 = 0;
      j1 = 1;
      k1 = 0;
      i2 = 0;
      j2 = 1;
      k2 = 1;
    } else {
      i1 = 0;
      j1 = 1;
      k1 = 0;
      i2 = 1;
      j2 = 1;
      k2 = 0;
    }
  }

  // Offsets from each simplex corner.
  const x1 = x0 - i1 + G3,
    y1 = y0 - j1 + G3,
    z1 = z0 - k1 + G3;
  const x2 = x0 - i2 + 2 * G3,
    y2 = y0 - j2 + 2 * G3,
    z2 = z0 - k2 + 2 * G3;
  const x3 = x0 - 1 + 3 * G3,
    y3 = y0 - 1 + 3 * G3,
    z3 = z0 - 1 + 3 * G3;

  // Gradient contributions — radial (0.6 − r²)⁴ kernel, clamped at 0.
  let n0 = 0,
    n1 = 0,
    n2 = 0,
    n3 = 0;
  let c: number;

  c = 0.6 - x0 * x0 - y0 * y0 - z0 * z0;
  if (c > 0) {
    c *= c;
    n0 = c * c * gradDot(hash3(i, j, k, seed), x0, y0, z0);
  }
  c = 0.6 - x1 * x1 - y1 * y1 - z1 * z1;
  if (c > 0) {
    c *= c;
    n1 = c * c * gradDot(hash3(i + i1, j + j1, k + k1, seed), x1, y1, z1);
  }
  c = 0.6 - x2 * x2 - y2 * y2 - z2 * z2;
  if (c > 0) {
    c *= c;
    n2 = c * c * gradDot(hash3(i + i2, j + j2, k + k2, seed), x2, y2, z2);
  }
  c = 0.6 - x3 * x3 - y3 * y3 - z3 * z3;
  if (c > 0) {
    c *= c;
    n3 = c * c * gradDot(hash3(i + 1, j + 1, k + 1, seed), x3, y3, z3);
  }

  return 32 * (n0 + n1 + n2 + n3);
}

/**
 * Ridged noise: `1 − 2|simplex|`, producing sharp positive ridges along
 * zero-crossings of the underlying field. Mapped back into [−1, 1].
 */
function ridged3(x: number, y: number, z: number, seed: number): number {
  return 1 - 2 * Math.abs(simplex3(x, y, z, seed));
}

// ---------------------------------------------------------------------------
// Voronoi F1 noise
// ---------------------------------------------------------------------------

/**
 * Voronoi / cellular F1 distance noise. Each integer lattice cell
 * contains one jittered feature point; we return the distance from
 * `(x, y, z)` to the nearest feature point across the 3×3×3 neighborhood,
 * remapped from [0, sqrt(3)] to [-1, 1] so it composes with the other
 * noise types under a shared `strength` scale.
 */
function voronoi3(x: number, y: number, z: number, seed: number): number {
  const ix = Math.floor(x),
    iy = Math.floor(y),
    iz = Math.floor(z);
  const fx = x - ix,
    fy = y - iy,
    fz = z - iz;
  let minDistSq = Infinity;
  for (let dz = -1; dz <= 1; dz++) {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const h = hash3(ix + dx, iy + dy, iz + dz, seed);
        // Three independent channels from one hash by rotating bits.
        const jx = hashToUnit(h);
        const jy = hashToUnit(Math.imul(h ^ 0x68e31da4, 0x85ebca6b) >>> 0);
        const jz = hashToUnit(Math.imul(h ^ 0xb5297a4d, 0xc2b2ae35) >>> 0);
        const px = dx + jx - fx;
        const py = dy + jy - fy;
        const pz = dz + jz - fz;
        const d = px * px + py * py + pz * pz;
        if (d < minDistSq) minDistSq = d;
      }
    }
  }
  // sqrt(3) ≈ 1.732 is the theoretical upper bound on F1 in this layout.
  // Remap [0, 1.732] → [1, -1] so near a feature point contributes "up".
  const dist = Math.sqrt(minDistSq);
  return 1 - (dist / 1.732) * 2;
}

// ---------------------------------------------------------------------------
// Worley (cellular F2−F1) noise
// ---------------------------------------------------------------------------

/**
 * Worley noise — F2−F1 distance variant of cellular noise.  Tracking the
 * two nearest feature-point distances and returning their difference creates
 * sharp raised ridges along cell boundaries and smooth valleys in the cell
 * interiors, producing crackle / cracked-mud / biological-membrane patterns
 * that are visually distinct from the smooth F1 Voronoi field.
 *
 * Output remapped from [0, ~0.8] (theoretical F2−F1 range) to [1, −1] so
 * that cell walls → +1 and deep interiors → −1, matching the sign convention
 * of the other noise types under a shared `strength` scale.
 */
function worley3(x: number, y: number, z: number, seed: number): number {
  const ix = Math.floor(x),
    iy = Math.floor(y),
    iz = Math.floor(z);
  const fx = x - ix,
    fy = y - iy,
    fz = z - iz;
  let f1Sq = Infinity,
    f2Sq = Infinity;
  for (let dz = -1; dz <= 1; dz++) {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const h = hash3(ix + dx, iy + dy, iz + dz, seed);
        const jx = hashToUnit(h);
        const jy = hashToUnit(Math.imul(h ^ 0x68e31da4, 0x85ebca6b) >>> 0);
        const jz = hashToUnit(Math.imul(h ^ 0xb5297a4d, 0xc2b2ae35) >>> 0);
        const px = dx + jx - fx;
        const py = dy + jy - fy;
        const pz = dz + jz - fz;
        const d = px * px + py * py + pz * pz;
        if (d < f1Sq) {
          f2Sq = f1Sq;
          f1Sq = d;
        } else if (d < f2Sq) {
          f2Sq = d;
        }
      }
    }
  }
  // Remap F2−F1 ∈ [0, ~0.8] → [1, −1]: cell walls ≈ 1, interiors ≈ −1.
  const diff = Math.sqrt(f2Sq) - Math.sqrt(f1Sq);
  return Math.max(-1, 1 - diff / 0.5);
}

// ---------------------------------------------------------------------------
// Layer evaluation
// ---------------------------------------------------------------------------

/**
 * Sample a layer's noise field at `(x, y, z)`. Returns a single scalar
 * in roughly [-1, 1]. Per-axis decorrelation is handled by the caller via
 * seed offsets, so each axis sees an independent noise channel.
 */
function sampleNoise(
  type: NoiseLayer["noiseType"],
  x: number,
  y: number,
  z: number,
  seed: number,
): number {
  switch (type) {
    case "simplex":
      return simplex3(x, y, z, seed);
    case "ridged":
      return ridged3(x, y, z, seed);
    case "voronoi":
      return voronoi3(x, y, z, seed);
    case "worley":
      return worley3(x, y, z, seed);
  }
}

// Per-axis seed offsets. Large odd integers chosen to avoid any lattice
// resonance between axes (don't use 1/2/3 — adjacent seeds produce visibly
// correlated Perlin fields along the same gradient table).
const SEED_OFFSET_X = 0;
const SEED_OFFSET_Y = 0x7f4a7c15;
const SEED_OFFSET_Z = 0x1b873593;

// ---------------------------------------------------------------------------
// Core
// ---------------------------------------------------------------------------

export function noiseCore(
  geo: ModifierGeometry,
  mod: BlendModifier,
): ModifierGeometry {
  if (mod.kind !== "noise") return geo;
  const noiseMod = mod as BlendNoiseModifier;
  const enabledLayers = noiseMod.layers.filter(
    (l) => l.enabled && l.strength !== 0,
  );
  if (enabledLayers.length === 0) return geo;

  const src = geo.positions;
  const vertCount = src.length / 3;
  // Single allocation — we mutate the copy in place, accumulating layers.
  const out = new Float32Array(src);

  // Compute the bounding box of the source geometry to derive a characteristic
  // size. This normalises both sample coordinates and displacement magnitude
  // to the object's own scale, so `scale` (≈ features-per-object) and
  // `strength` (fraction of object size) behave identically regardless of
  // whether the mesh is measured in millimetres or metres. Falls back to 1
  // when the geometry is degenerate (empty mesh or all vertices coincident).
  let minX = Infinity,
    minY = Infinity,
    minZ = Infinity;
  let maxX = -Infinity,
    maxY = -Infinity,
    maxZ = -Infinity;
  for (let i = 0; i < src.length; i += 3) {
    const px = src[i],
      py = src[i + 1],
      pz = src[i + 2];
    if (px < minX) minX = px;
    if (px > maxX) maxX = px;
    if (py < minY) minY = py;
    if (py > maxY) maxY = py;
    if (pz < minZ) minZ = pz;
    if (pz > maxZ) maxZ = pz;
  }
  const charSize = Math.max(maxX - minX, maxY - minY, maxZ - minZ) || 1;
  const invCharSize = 1 / charSize;

  for (const layer of enabledLayers) {
    const wantX = layer.axisX;
    const wantY = layer.axisY;
    const wantZ = layer.axisZ;
    if (!wantX && !wantY && !wantZ) continue;

    // `scale` = approximate number of noise features spanning the object.
    // Mapping: sampleCoord = (localPos / charSize) * scale
    // scale = 0 → all vertices land on the same noise cell → no-op.
    const sampleScale = layer.scale > 0 ? layer.scale * invCharSize : 0;
    if (sampleScale === 0) continue;

    // `strength` = peak displacement as a fraction of the object's largest
    // dimension. Multiply by charSize to convert back to world-space metres.
    const layerStrength = layer.strength * charSize;
    const baseSeed = layer.seed | 0;
    const sx = (baseSeed + SEED_OFFSET_X) | 0;
    const sy = (baseSeed + SEED_OFFSET_Y) | 0;
    const sz = (baseSeed + SEED_OFFSET_Z) | 0;
    const type = layer.noiseType;

    // Vertex group mask — when set, we build a Uint8Array mask once per
    // layer so the hot loop is a single branch.
    let mask: Uint8Array | null = null;
    if (layer.vertexGroupIndices && layer.vertexGroupIndices.length > 0) {
      mask = new Uint8Array(vertCount);
      const idxs = layer.vertexGroupIndices;
      for (let k = 0; k < idxs.length; k++) {
        const vi = idxs[k];
        if (vi < vertCount) mask[vi] = 1;
      }
    }

    for (let v = 0; v < vertCount; v++) {
      if (mask && mask[v] === 0) continue;
      const o = v * 3;
      // Sample at the *original* vertex position, not the accumulated
      // one — layers should read a stable coordinate field, otherwise
      // later layers drift across the earlier layer's displacement.
      const x = src[o] * sampleScale;
      const y = src[o + 1] * sampleScale;
      const z = src[o + 2] * sampleScale;
      if (wantX) out[o] += layerStrength * sampleNoise(type, x, y, z, sx);
      if (wantY) out[o + 1] += layerStrength * sampleNoise(type, x, y, z, sy);
      if (wantZ) out[o + 2] += layerStrength * sampleNoise(type, x, y, z, sz);
    }
  }

  return {
    ...geo,
    positions: out,
    // Normals are invalidated by the displacement — null them out so the
    // finalizer recomputes from the new positions.
    normals: null,
    provenance: identityProvenance(vertCount),
  };
}
